import { DetailsPanel, LoadingIcon } from '@/components/controls';
import { useNichePersonImage, useSubject } from '@/hooks';
import { toProperCase } from '@/utils';
import {
  ElectricBolt as ElectricBoltIcon,
  Facebook as FacebookIcon,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardHeader,
  CardMedia,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import {
  DesktopClassic as DesktopClassicIcon,
  Pistol as PistolIcon,
} from 'mdi-material-ui';
import { FlagAvatar } from './FlagAvatar';

export function PrisonRecall({ recall, onOptionClick }) {
  const subjectType = 'PRISON_RECALL';
  const {
    query: {
      data: imageSrc,
      isLoading: imageLoading,
      isFetching: imageFetching,
    },
  } = useNichePersonImage(recall?.personId);
  const {
    query: {
      data: subject,
      isLoading: subjectLoading,
      isFetching: subjectFetching,
    },
  } = useSubject(subjectType, recall?.id);

  return (
    <Card variant="outlined" sx={{ minWidth: 240 }}>
      <CardHeader
        avatar={<FlagAvatar code="PRC" />}
        title={toProperCase(
          [recall?.forenames, recall?.surname].filter(Boolean).join(' '),
        )}
        action={
          <LoadingIcon
            loading={
              subjectLoading || subjectFetching || imageLoading || imageFetching
            }
          />
        }
      />
      {imageSrc ? (
        <CardMedia
          component="img"
          sx={{ minHeight: 240, width: '100%' }}
          image={imageSrc}
          alt="Photo of recall subject"
        />
      ) : (
        <Box
          sx={(theme) => ({
            height: 240,
            width: '100%',
            bgcolor: theme.palette.mode === 'dark' ? 'grey.600' : 'grey.300',
          })}
        />
      )}
      <Stack spacing={1} sx={{ p: 1 }}>
        <Stack
          direction="row"
          spacing={0.5}
          sx={{ alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography
            variant="body3"
            color="textSecondary"
            sx={{ flexGrow: 1 }}
          >
            {recall?.occurrenceNumber}
          </Typography>
        </Stack>
        {recall?.outstandingForOtherMatters && (
          <Stack direction="row" spacing={1}>
            <Chip label="Outstanding for other matters" size="small" />
          </Stack>
        )}
        <DetailsPanel
          entries={[
            {
              label: 'Last log entry',
              value: recall?.lastLogEntryTime
                ? format(recall.lastLogEntryTime, 'dd/MM/yyyy HH:mm')
                : '',
            },
          ]}
        />
        <Stack
          direction="row"
          spacing={1}
          sx={{ justifyContent: 'flex-end', alignItems: 'center' }}
        >
          <Tooltip
            title={
              subject?.pncCirculated ? (
                <Stack>
                  <Typography variant="caption">Circulated on PNC</Typography>
                  <Typography variant="caption">
                    {subject.pncCirculated.username}
                  </Typography>
                  <Typography variant="caption">
                    {format(subject.pncCirculated.time, 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
              ) : (
                'Not circulated on PNC'
              )
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                recall?.id,
                subject?.code,
                'pncCirculated',
              )}
              color={subject?.pncCirculated ? 'primary' : 'default'}
            >
              <DesktopClassicIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.socialMediaCirculated ? (
                <Stack>
                  <Typography variant="caption">
                    Circulated on social media
                  </Typography>
                  <Typography variant="caption">
                    {subject.socialMediaCirculated.username}
                  </Typography>
                  <Typography variant="caption">
                    {format(
                      subject.socialMediaCirculated.time,
                      'dd/MM/yyyy HH:mm',
                    )}
                  </Typography>
                </Stack>
              ) : (
                'Not circulated on social media'
              )
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                recall?.id,
                subject?.code,
                'socialMediaCirculated',
              )}
              color={subject?.socialMediaCirculated ? 'primary' : 'default'}
            >
              <FacebookIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.taser ? (
                <Stack>
                  <Typography variant="caption">Taser officers only</Typography>
                  <Typography variant="caption">
                    {subject.taser.username}
                  </Typography>
                  <Typography variant="caption">
                    {format(subject.taser.time, 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
              ) : (
                'No Taser authority'
              )
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                recall?.id,
                subject?.code,
                'taser',
              )}
              color={subject?.taser ? 'primary' : 'default'}
            >
              <ElectricBoltIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
          <Tooltip
            title={
              subject?.firearms ? (
                <Stack>
                  <Typography variant="caption">
                    Firearms officers only
                  </Typography>
                  <Typography variant="caption">
                    {subject.firearms.username}
                  </Typography>
                  <Typography variant="caption">
                    {format(subject.firearms.time, 'dd/MM/yyyy HH:mm')}
                  </Typography>
                </Stack>
              ) : (
                'No Firearms authority'
              )
            }
          >
            <IconButton
              size="small"
              onClick={onOptionClick(
                subjectType,
                recall?.id,
                subject?.code,
                'firearms',
              )}
              color={subject?.firearms ? 'primary' : 'default'}
            >
              <PistolIcon sx={{ fontSize: 'inherit' }} />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>
    </Card>
  );
}
